import { Component, ElementRef, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { DataManagementService } from '../../data-management/data-management.service';
import { HistoricalService } from '../historical.service';
import { VideoService } from '../../realtime/video.service';
import { HeaderService } from 'src/app/header/header.service';
import { SigninService } from 'src/app/signin/signin.service';
import { IonModal, IonRouterOutlet, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Group, Vehicule } from 'src/app/model/data-managemnt';
import { DateInterval } from 'src/app/model/date-interval.model';
import { videoUrl } from 'src/app/global.config';
import { MapService } from 'src/app/utils/leaflet/service/map.service';
import { Map } from 'leaflet';
import { DraggableSheetComponent } from 'src/app/shared/components/draggable-sheet/draggable-sheet.component';
import {
  alignSeekToPlaybackChunkStart,
  calendarFieldsAsCameraGmtMillis,
  formatAlertClipFilenameParts,
  formatCameraGmtDateTime,
  formatCameraGmtTickParts,
  formatDateToPlaybackMapToken,
  formatPlaybackSeekToken,
  findPlaybackMapRangeContaining,
  parsePlaybackMapTokenToMillis,
  PlaybackMapRange,
  segmentDurationLabel
} from 'src/app/utils/video-playback/video-playback.utils';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RealtimeWorkerService } from 'src/app/pages/realtime/realtime-worker.service';
import * as moment from 'moment';
import { VideoOrientationService } from 'src/app/utils/video-orientation/video-orientation.service';
import { MapNavigationService } from 'src/app/utils/map-top-toolbar/map-navigation.service';

interface TimelineSegmentLayout {
  range: PlaybackMapRange;
  leftPct: number;
  widthPct: number;
  tooltip: string;
}

interface TimelineTick {
  leftPct: number;
  align: 'start' | 'center' | 'end';
  dateLabel: string;
  timeLabel: string;
}

interface TimelineEventFlag {
  wallMs: number;
  leftPct: number;
  addedInfo: number;
  ionIcon: string;
  color: string;
  title: string;
  event: any;
}

@Component({
  selector: 'app-video-playback-historical',
  templateUrl: './video-playback-historical.component.html',
  styleUrls: ['./video-playback-historical.component.scss'],
})
export class VideoPlaybackHistoricalComponent implements OnInit, OnDestroy {
  @ViewChild('playback_video') playbackVideoRef: ElementRef<HTMLVideoElement>;
  @ViewChild('draggableSheet') draggableSheet: DraggableSheetComponent;
  @ViewChild('playbackPanel') playbackPanelRef: ElementRef<HTMLElement>;
  @ViewChild('playbackPanelHost') playbackPanelHostRef: ElementRef<HTMLElement>;
  @ViewChild('alertVideoWrap') alertVideoWrapRef: ElementRef<HTMLElement>;
  @ViewChild('alertVideoHost') alertVideoHostRef: ElementRef<HTMLElement>;
  @ViewChild('startDateModal') startDateModal: IonModal;
  @ViewChild('endDateModal') endDateModal: IonModal;
  @ViewChild('alertVideo') set alertVideo(ref: ElementRef<HTMLVideoElement> | undefined) {
    if (ref?.nativeElement) {
      this.attachAlertVideoFullscreenListeners(ref.nativeElement);
    } else {
      this.detachAlertVideoFullscreenListeners();
    }
  }

  loading = false;
  isWakingUp = false;
  wakeupTimeRemaining = 0;
  ranges: PlaybackMapRange[] = [];
  periodEvents: any[] = [];
  videoPlaybackMap: Map;

  windowStartMs: number | null = null;
  windowEndMs: number | null = null;
  private timelineZoomStartMs: number | null = null;
  private timelineZoomEndMs: number | null = null;
  timelineDisplayStart = '';
  timelineDisplayEnd = '';
  timelineSegments: TimelineSegmentLayout[] = [];
  timelineTicks: TimelineTick[] = [];
  timelineEventFlags: TimelineEventFlag[] = [];

  zoomSelecting = false;
  zoomDragStartPct: number | null = null;
  zoomDragCurrentPct: number | null = null;
  private zoomTrackEl: HTMLElement | null = null;
  private readonly boundZoomMove = (e: PointerEvent) => this.onTimelineZoomPointerMove(e);
  private readonly boundZoomUp = (e: PointerEvent) => this.onTimelineZoomPointerUp(e);
  private startDismissTimer: ReturnType<typeof setTimeout> | null = null;
  private endDismissTimer: ReturnType<typeof setTimeout> | null = null;
  private startOpenSnapshot: string | null = null;
  private endOpenSnapshot: string | null = null;

  hoverActive = false;
  hoverLeftPct = 0;
  hoverDateLabel = '';

  selectedTimelineEvent: any = null;
  selectedTimelineEventVideoUrl = '';

  /** Inline camera playback under the timeline (fleet-12 frise seek, no map simulation). */
  showPlaybackPlayer = false;
  /** User tapped expand icon — portrait fullscreen overlay. */
  isVideoFullscreen = false;
  /** Phone is physically in landscape while video is open. */
  isDeviceLandscape = false;
  /** iOS did not rotate the WebView — CSS-rotate the fullscreen player. */
  iosFakeLandscape = false;
  iosRotateLeft = false;
  /** After closing FS, keep player inline until the phone is portrait again. */
  private suppressLandscapeExpand = false;
  /** Alert clip uses an in-app overlay on iOS (native AVPlayer ignores app orientation). */
  isAlertVideoFullscreen = false;
  activeRecordingRange: PlaybackMapRange | null = null;
  selectedCamera: 'driver' | 'front' | 'rear' = 'front';
  trackPlaybackProgress = 0;
  trackPlaybackWallMs: number | null = null;
  private playbackAnchorWallMs: number | null = null;
  private playbackAnchorRealMs: number | null = null;
  private videoPlaybackTickInterval: ReturnType<typeof setInterval> | null = null;
  private lastEmittedPlaybackSecond = -1;

  segmentDurationLabel = segmentDurationLabel;

  private wakeupSubscription: Subscription | null = null;
  private wakeupTimeSubscription: Subscription | null = null;
  private alertVideoEl: HTMLVideoElement | null = null;
  private ignoringNativeFullscreenExit = false;
  private orientationSessionActive = false;
  private readonly onViewportRelayout = (): void => this.scheduleSheetRelayoutAfterOrientation();

  constructor(
    public dataManagementService: DataManagementService,
    public historicalService: HistoricalService,
    public signinService: SigninService,
    public videoService: VideoService,
    private headerService: HeaderService,
    private mapService: MapService,
    private toastController: ToastController,
    private translate: TranslateService,
    private realTimeWorker: RealtimeWorkerService,
    private videoOrientationService: VideoOrientationService,
    private mapNavigationService: MapNavigationService,
    @Optional() private routerOutlet?: IonRouterOutlet,
  ) {}

  ngOnInit(): void {
    this.historicalService.selectedTab = 'video-playback';
    this.dataManagementService.init();
    this.applyWindowFromHistoricalDates();
    this.subscribeToCameraWakeupState();
    window.addEventListener('orientationchange', this.onViewportRelayout);
    window.addEventListener('resize', this.onViewportRelayout);
  }

  ngOnDestroy(): void {
    window.removeEventListener('orientationchange', this.onViewportRelayout);
    window.removeEventListener('resize', this.onViewportRelayout);
    this.clearPlaybackDateDismissTimers();
    this.detachTimelineZoomListeners();
    this.stopVideoPlaybackTickLoop();
    this.detachAlertVideoFullscreenListeners();
    this.releaseVideoOverlays();
    void this.exitVideoFullscreen();
    this.isAlertVideoFullscreen = false;
    this.orientationSessionActive = false;
    void this.videoOrientationService.disableVideoRotation();
    this.syncMapChromeForPlayback(false);
    this.wakeupSubscription?.unsubscribe();
    this.wakeupTimeSubscription?.unsubscribe();
    this.videoService.teardownPlaybackMapRequests();
    this.videoService.teardownVideoSession(this.dataManagementService.selectedVehicule);
  }

  ionViewDidEnter(): void {
    if (this.routerOutlet) {
      this.routerOutlet.swipeGesture = false;
    }
    // Maximize pull-up whenever the video-playback page is opened / re-entered.
    setTimeout(() => this.draggableSheet?.expandMaximized(), 0);

    if (this.mapService.preventMapDestroy) {
      this.mapService.preventMapDestroy = false;
      setTimeout(() => {
        if (this.mapService.map) {
          this.mapService.map.invalidateSize();
          this.realTimeWorker.syncSelectedVehicleMarkerOnMap();
        }
      }, 150);
      return;
    }
    this.mapService.initMap(this.videoPlaybackMap, 'videoPlaybackMap');
    setTimeout(() => {
      if (this.mapService.map) {
        this.mapService.map.invalidateSize();
        this.realTimeWorker.syncSelectedVehicleMarkerOnMap();
      }
    }, 150);
  }

  ionViewWillLeave(): void {
    if (this.routerOutlet) {
      this.routerOutlet.swipeGesture = true;
    }
    this.detachTimelineZoomListeners();
    this.stopVideoPlaybackTickLoop();
    this.detachAlertVideoFullscreenListeners();
    this.releaseVideoOverlays();
    void this.exitVideoFullscreen();
    this.isAlertVideoFullscreen = false;
    this.orientationSessionActive = false;
    void this.videoOrientationService.disableVideoRotation();
    this.syncMapChromeForPlayback(false);
    if (!this.mapService.preventMapDestroy && this.mapService.map) {
      this.mapService.map.remove();
    }
  }

  get isTimelineZoomed(): boolean {
    return this.timelineZoomStartMs != null && this.timelineZoomEndMs != null;
  }

  get trackSegmentLeftPct(): number {
    if (!this.showPlaybackPlayer || !this.activeRecordingRange) {
      return 0;
    }
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    const t0 = this.trackSegmentStartMs;
    if (t0 == null) {
      return 0;
    }
    return Math.max(0, Math.min(100, ((t0 - w0) / span) * 100));
  }

  get trackSegmentWidthPct(): number {
    if (!this.showPlaybackPlayer || !this.activeRecordingRange) {
      return 0;
    }
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null || t1 <= t0) {
      return 0;
    }
    return Math.max(0, Math.min(100 - this.trackSegmentLeftPct, ((t1 - t0) / span) * 100));
  }

  get trackPlaybackPastLeftPct(): number {
    return this.trackSegmentLeftPct;
  }

  get trackPlaybackPastWidthPct(): number {
    return (this.trackSegmentWidthPct * this.trackPlaybackProgress) / 100;
  }

  get trackPlaybackCursorPct(): number {
    return this.trackSegmentLeftPct + this.trackPlaybackPastWidthPct;
  }

  get trackPlaybackCursorLabel(): string {
    if (this.trackPlaybackWallMs != null) {
      return formatCameraGmtDateTime(this.trackPlaybackWallMs, true);
    }
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null) {
      return '';
    }
    const wallMs = Math.round(t0 + ((t1 - t0) * this.trackPlaybackProgress) / 100);
    return formatCameraGmtDateTime(wallMs, true);
  }

  private get trackSegmentStartMs(): number | null {
    if (!this.activeRecordingRange?.from) {
      return null;
    }
    const ms = parsePlaybackMapTokenToMillis(this.activeRecordingRange.from);
    return Number.isFinite(ms) ? ms : null;
  }

  private get trackSegmentEndMs(): number | null {
    if (!this.activeRecordingRange?.to) {
      return null;
    }
    const ms = parsePlaybackMapTokenToMillis(this.activeRecordingRange.to);
    return Number.isFinite(ms) ? ms : null;
  }

  get zoomBoxLeftPct(): number {
    if (!this.zoomSelecting || this.zoomDragStartPct == null || this.zoomDragCurrentPct == null) {
      return 0;
    }
    return Math.min(this.zoomDragStartPct, this.zoomDragCurrentPct);
  }

  get zoomBoxWidthPct(): number {
    if (!this.zoomSelecting || this.zoomDragStartPct == null || this.zoomDragCurrentPct == null) {
      return 0;
    }
    return Math.abs(this.zoomDragCurrentPct - this.zoomDragStartPct);
  }

  chooseGroup(group: Group): void {
    this.dataManagementService.selectedGroup = group;
    if (group?.vehicules?.length) {
      const vehicule = group.vehicules[0];
      this.dataManagementService.setSelectedVehicle(vehicule, group);
      this.realTimeWorker.applyHistoriqueVehicleSelection(vehicule, group);
    }
    this.clearResults();
  }

  chooseVehicule(vehicule: Vehicule): void {
    this.dataManagementService.setSelectedVehicle(vehicule, this.dataManagementService.selectedGroup);
    this.realTimeWorker.applyHistoriqueVehicleSelection(vehicule, this.dataManagementService.selectedGroup);
    this.clearResults();
  }

  get startDateIso(): string {
    return this.toIsoLocal(this.historicalService.startDate);
  }

  get endDateIso(): string {
    return this.toIsoLocal(this.historicalService.endDate);
  }

  onPlaybackDateModalOpen(type: 'start' | 'end'): void {
    const snapshot = this.toPickerSnapshot(type === 'start' ? this.startDateIso : this.endDateIso);
    if (type === 'start') {
      this.startOpenSnapshot = snapshot;
      if (this.startDismissTimer) {
        clearTimeout(this.startDismissTimer);
        this.startDismissTimer = null;
      }
    } else {
      this.endOpenSnapshot = snapshot;
      if (this.endDismissTimer) {
        clearTimeout(this.endDismissTimer);
        this.endDismissTimer = null;
      }
    }
  }

  onPlaybackDateChange(event: CustomEvent, type: 'start' | 'end'): void {
    const value = (event?.detail as { value?: string | string[] })?.value;
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw) {
      return;
    }
    const next = new Date(raw);
    if (Number.isNaN(next.getTime())) {
      return;
    }
    const nextSnapshot = this.toPickerSnapshot(next);
    const openSnapshot = type === 'start' ? this.startOpenSnapshot : this.endOpenSnapshot;
    // Ignore the synthetic change fired when the picker opens.
    if (openSnapshot && openSnapshot === nextSnapshot) {
      return;
    }

    if (type === 'start') {
      this.historicalService.startDate = next;
      if (this.historicalService.endDate && next > this.historicalService.endDate) {
        this.historicalService.endDate = new Date(next);
      }
    } else {
      this.historicalService.endDate = next;
      if (this.historicalService.startDate && next < this.historicalService.startDate) {
        this.historicalService.startDate = new Date(next);
      }
    }
    this.clearResults();
    this.schedulePlaybackDateModalDismiss(type);
  }

  private toPickerSnapshot(value: Date | string | number | null | undefined): string {
    const d = value instanceof Date ? value : new Date(value ?? Date.now());
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}T${d.getHours()}:${d.getMinutes()}`;
  }

  private schedulePlaybackDateModalDismiss(type: 'start' | 'end'): void {
    const modal = type === 'start' ? this.startDateModal : this.endDateModal;
    const timer = setTimeout(() => {
      if (type === 'start') {
        this.startOpenSnapshot = null;
        this.startDismissTimer = null;
      } else {
        this.endOpenSnapshot = null;
        this.endDismissTimer = null;
      }
      void modal?.dismiss();
    }, 450);

    if (type === 'start') {
      if (this.startDismissTimer) {
        clearTimeout(this.startDismissTimer);
      }
      this.startDismissTimer = timer;
    } else {
      if (this.endDismissTimer) {
        clearTimeout(this.endDismissTimer);
      }
      this.endDismissTimer = timer;
    }
  }

  private clearPlaybackDateDismissTimers(): void {
    if (this.startDismissTimer) {
      clearTimeout(this.startDismissTimer);
      this.startDismissTimer = null;
    }
    if (this.endDismissTimer) {
      clearTimeout(this.endDismissTimer);
      this.endDismissTimer = null;
    }
  }

  private toIsoLocal(date: Date | string | number | null | undefined): string {
    const d = date ? new Date(date) : new Date();
    if (Number.isNaN(d.getTime())) {
      return new Date().toISOString();
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  clearResults(): void {
    this.hidePlaybackPlayer();
    this.ranges = [];
    this.periodEvents = [];
    this.timelineSegments = [];
    this.timelineTicks = [];
    this.timelineEventFlags = [];
    this.clearTimelineEventDetails();
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;
    this.applyWindowFromHistoricalDates();
  }

  resetTimelineZoom(): void {
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;
    this.rebuildTimeline();
  }

  async loadRecordingRanges(): Promise<void> {
    const vehicule = this.dataManagementService.selectedVehicule;
    if (!vehicule?.device?.streamId) {
      await this.showToast(this.translate.instant('HISTORICAL.videoPlaybackNoStreamId'), 'warning');
      return;
    }

    if (!this.applyWindowFromHistoricalDates()) {
      await this.showToast(this.translate.instant('HISTORICAL.videoPlaybackPickRange'), 'warning');
      return;
    }

    const startDate = new Date(this.windowStartMs);
    const endDate = new Date(this.windowEndMs);
    const startTok = formatDateToPlaybackMapToken(startDate);
    const endTok = formatDateToPlaybackMapToken(endDate);
    const dateInterval: DateInterval = { startDate, endDate };
    const deviceId = vehicule.device.idDevice;

    this.loading = true;
    this.ranges = [];
    this.periodEvents = [];
    this.clearTimelineEventDetails();
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;

    try {
      this.videoService.vehicule = vehicule;
      await this.videoService.ensureSignalingForVehicle(vehicule);

      const { mapRes, events } = await forkJoin({
        mapRes: this.videoService.requestPlaybackMap(startTok, endTok),
        events: this.headerService.getEventsByDeviceAndDateRange(deviceId, dateInterval).pipe(
          catchError(() => of([]))
        )
      }).toPromise();

      this.ranges = mapRes?.ranges || [];
      const rawEvents = Array.isArray(events) ? events : events?.data || [];
      this.periodEvents = rawEvents.filter((e: any) => this.isRecognizedEvent(e?.addedInfo));
      this.rebuildTimeline();

      const segCount = this.ranges.length;
      const evCount = this.periodEvents.length;
      if (!segCount && !evCount) {
        await this.showToast(this.translate.instant('HISTORICAL.videoPlaybackNoRanges'), 'info');
      } else if (!segCount && evCount) {
        await this.showToast(
          this.translate.instant('HISTORICAL.videoPlaybackEventsNoRanges', { events: evCount }),
          'info'
        );
      } else {
        await this.showToast(
          this.translate.instant('HISTORICAL.videoPlaybackRangesAndEvents', {
            segments: segCount,
            events: evCount
          }),
          'success'
        );
      }
    } catch (err: any) {
      await this.showToast(
        `${this.translate.instant('HISTORICAL.videoPlaybackMapFailed')}: ${err?.message || err}`,
        'danger'
      );
    } finally {
      this.loading = false;
    }
  }

  onRecordingSegmentClick(seg: TimelineSegmentLayout, ev: Event): void {
    ev.stopPropagation();
    ev.preventDefault();
    this.clearTimelineEventDetails();
    this.openSegmentPlayback(seg.range);
  }

  openSegmentPlayback(range: PlaybackMapRange): void {
    const vehicule = this.dataManagementService.selectedVehicule;
    if (!vehicule?.device?.streamId) {
      void this.showToast(this.translate.instant('VIDEO.error.noVehicle'), 'warning');
      return;
    }
    const startMs = parsePlaybackMapTokenToMillis(range.from);
    const endMs = parsePlaybackMapTokenToMillis(range.to);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      void this.showToast(this.translate.instant('HISTORICAL.videoPlaybackNoRanges'), 'warning');
      return;
    }

    this.clearTimelineEventDetails();
    this.activeRecordingRange = range;
    this.showPlaybackPlayer = true;
    this.syncMapChromeForPlayback(true);
    this.syncOrientationSession();
    this.trackPlaybackProgress = 0;
    this.trackPlaybackWallMs = startMs;
    this.playbackAnchorWallMs = startMs;
    this.playbackAnchorRealMs = null;
    this.selectedCamera = this.signinService.hasVehicleFrontCameraOption(vehicule) ? 'front' : 'driver';

    this.videoService.vehicule = vehicule;
    this.videoService.type = 'playback';
    this.videoService.playbackMapRanges = this.ranges?.length ? [...this.ranges] : [range];
    this.videoService.recordingRange = range;
    this.videoService.setPlaybackRange(startMs, endMs, range);
    this.videoService.returnRoute = '/video-playback';

    setTimeout(() => {
      this.scrollSheetToPlayback();
      this.bindPlaybackVideoElement();
      const channel = this.getPlaybackChannel(this.selectedCamera);
      void this.videoService.startHistoricalPlayback(channel)
        .then(() => this.startVideoPlaybackTickLoop())
        .catch((err) => console.error('Failed to start historical playback', err));
    }, 80);
  }

  private scrollSheetToPlayback(): void {
    const panel =
      this.playbackPanelRef?.nativeElement ||
      this.playbackPanelHostRef?.nativeElement;
    this.draggableSheet?.scrollToElement(panel);
  }

  hidePlaybackPlayer(): void {
    this.stopVideoPlaybackTickLoop();
    this.releaseVideoOverlays();
    void this.exitVideoFullscreen();
    this.showPlaybackPlayer = false;
    this.syncOrientationSession();
    const vehicule = this.dataManagementService.selectedVehicule;
    if (this.videoService.connexion || this.videoService.playback || this.videoService.loadingStream) {
      this.videoService.teardownVideoSession(vehicule);
    }
    this.showPlaybackPlayer = false;
    this.syncMapChromeForPlayback(false);
    this.activeRecordingRange = null;
    this.trackPlaybackProgress = 0;
    this.trackPlaybackWallMs = null;
    this.playbackAnchorWallMs = null;
    this.playbackAnchorRealMs = null;
  }

  toggleVideoFullscreen(): void {
    if (this.isVideoFullscreen || this.isVideoExpanded()) {
      this.collapsePlaybackToInline();
      return;
    }
    this.suppressLandscapeExpand = false;
    this.isVideoFullscreen = true;
    this.syncOrientationSession();
    setTimeout(() => this.refreshPlaybackVideoLayout(), 0);
  }

  /** Exit custom fullscreen and put the camera player back in the sheet. */
  private collapsePlaybackToInline(): void {
    this.isVideoFullscreen = false;
    this.isDeviceLandscape = false;
    this.iosFakeLandscape = false;
    this.iosRotateLeft = false;
    this.suppressLandscapeExpand = true;
    this.syncOrientationSession();
    this.placeOverlay(
      this.playbackPanelRef?.nativeElement,
      this.playbackPanelHostRef?.nativeElement,
      false,
    );
    document.body.classList.remove('vp-video-fs');
    this.cleanupOrphanedVideoOverlays();
    this.finishRestoreAfterFullscreen('playback');
  }

  private applyVideoOrientationLayout(landscape: boolean, cssRotateDeg: 0 | 90 | -90 = 0): void {
    if (!this.showPlaybackPlayer && !this.isAlertVideoFullscreen) {
      return;
    }

    const wasLandscapeExpanded =
      this.isDeviceLandscape || this.iosFakeLandscape || this.isVideoFullscreen;

    // User closed fullscreen: stay inline while the phone is still landscape.
    if (this.suppressLandscapeExpand && this.showPlaybackPlayer && !this.isAlertVideoFullscreen) {
      if (!landscape) {
        this.suppressLandscapeExpand = false;
        this.refreshPlaybackVideoLayout();
        // Portrait again after rotate — re-dock sheet (same bug as closing fullscreen).
        this.finishRestoreAfterFullscreen(this.selectedTimelineEvent ? 'alert' : 'playback');
      } else {
        this.isDeviceLandscape = false;
        this.iosFakeLandscape = false;
        this.iosRotateLeft = false;
        this.refreshPlaybackVideoLayout();
      }
      return;
    }

    this.isDeviceLandscape = landscape;
    this.iosFakeLandscape = cssRotateDeg !== 0;
    this.iosRotateLeft = cssRotateDeg < 0;
    this.refreshPlaybackVideoLayout();

    // Rotated back to portrait: put video inline and re-dock the bottom sheet.
    if (!landscape && wasLandscapeExpanded) {
      this.isVideoFullscreen = false;
      this.isDeviceLandscape = false;
      this.iosFakeLandscape = false;
      this.iosRotateLeft = false;
      this.cleanupOrphanedVideoOverlays();
      document.body.classList.remove('vp-video-fs');
      this.refreshPlaybackVideoLayout();
      const target: 'playback' | 'alert' =
        this.isAlertVideoFullscreen || !!this.selectedTimelineEvent ? 'alert' : 'playback';
      // Wait for viewport metrics after orientation lock/resize.
      setTimeout(() => this.finishRestoreAfterFullscreen(target), 50);
      setTimeout(() => this.draggableSheet?.relayoutAfterFullscreen(), 250);
      setTimeout(() => this.draggableSheet?.relayoutAfterFullscreen(), 500);
    }
  }

  /** True when the video panel should cover the screen (button or phone rotated). */
  isVideoExpanded(): boolean {
    return this.isVideoFullscreen || (this.showPlaybackPlayer && this.isDeviceLandscape);
  }

  useIosFakeLandscape(): boolean {
    return this.iosFakeLandscape && (this.isVideoExpanded() || this.isAlertVideoFullscreen);
  }

  private syncOrientationSession(): void {
    const needRotation = this.showPlaybackPlayer || this.isAlertVideoFullscreen;
    if (needRotation && !this.orientationSessionActive) {
      this.orientationSessionActive = true;
      void this.videoOrientationService.enableVideoRotation((state) => {
        this.applyVideoOrientationLayout(state.landscape, state.cssRotateDeg);
      });
    } else if (!needRotation && this.orientationSessionActive) {
      this.orientationSessionActive = false;
      this.iosFakeLandscape = false;
      this.iosRotateLeft = false;
      this.isDeviceLandscape = false;
      void this.videoOrientationService.disableVideoRotation();
    }
  }

  private async exitVideoFullscreen(): Promise<void> {
    this.isVideoFullscreen = false;
    this.isDeviceLandscape = false;
    this.iosFakeLandscape = false;
    this.iosRotateLeft = false;
    this.refreshPlaybackVideoLayout();
  }

  private finishRestoreAfterFullscreen(target: 'playback' | 'alert'): void {
    this.draggableSheet?.relayoutAfterFullscreen();
    const scroll = () => {
      if (target === 'playback') {
        this.bindPlaybackVideoElement();
        this.scrollSheetToPlayback();
        return;
      }
      const panel = document.querySelector('.vp-timeline-event-panel') as HTMLElement | null;
      const video = document.querySelector('.vp-timeline-event-panel__video-wrap') as HTMLElement | null;
      this.draggableSheet?.scrollToElement(video || panel);
    };
    setTimeout(scroll, 80);
    setTimeout(scroll, 280);
    setTimeout(scroll, 500);
  }

  /** After rotate back to portrait, re-dock sheet even if video is not expanded. */
  private scheduleSheetRelayoutAfterOrientation(): void {
    // While landscape fullscreen/overlay is active, don't fight the video layout.
    if (this.isVideoExpanded() || this.isAlertVideoFullscreen) {
      return;
    }
    const portrait = window.innerHeight >= window.innerWidth;
    if (!portrait) {
      return;
    }
    this.cleanupOrphanedVideoOverlays();
    setTimeout(() => this.draggableSheet?.relayoutAfterFullscreen(), 80);
    setTimeout(() => this.draggableSheet?.relayoutAfterFullscreen(), 300);
    setTimeout(() => {
      this.draggableSheet?.relayoutAfterFullscreen();
      if (this.showPlaybackPlayer) {
        this.scrollSheetToPlayback();
      } else if (this.selectedTimelineEvent) {
        const panel = document.querySelector('.vp-timeline-event-panel') as HTMLElement | null;
        this.draggableSheet?.scrollToElement(panel);
      }
    }, 550);
  }

  private restoreSheetAfterVideoFullscreen(target: 'playback' | 'alert'): void {
    this.finishRestoreAfterFullscreen(target);
  }

  private cleanupOrphanedVideoOverlays(): void {
    // Panels may have been moved to <body> for fullscreen — return them if still orphaned.
    this.placeOverlay(
      this.playbackPanelRef?.nativeElement,
      this.playbackPanelHostRef?.nativeElement,
      false,
    );
    this.placeOverlay(
      this.alertVideoWrapRef?.nativeElement,
      this.alertVideoHostRef?.nativeElement,
      false,
    );
    document.querySelectorAll('.vp-playback-panel.vp-playback-panel--fullscreen, .vp-alert-video--fullscreen').forEach((el) => {
      el.classList.remove('vp-playback-panel--fullscreen', 'vp-alert-video--fullscreen', 'vp-ios-fake-landscape', 'vp-ios-fake-landscape--left');
    });
  }

  private refreshPlaybackVideoLayout(): void {
    const playbackExpanded = this.isVideoExpanded();
    this.placeOverlay(
      this.playbackPanelRef?.nativeElement,
      this.playbackPanelHostRef?.nativeElement,
      playbackExpanded,
    );
    this.placeOverlay(
      this.alertVideoWrapRef?.nativeElement,
      this.alertVideoHostRef?.nativeElement,
      this.isAlertVideoFullscreen,
    );
    document.body.classList.toggle(
      'vp-video-fs',
      playbackExpanded || this.isAlertVideoFullscreen,
    );
    const video = this.playbackVideoRef?.nativeElement;
    if (!video) {
      return;
    }
    void video.offsetHeight;
    setTimeout(() => void video.offsetHeight, 100);
    setTimeout(() => void video.offsetHeight, 400);
  }

  private placeOverlay(el: HTMLElement | undefined, host: HTMLElement | undefined, overlay: boolean): void {
    if (!el || !host) {
      return;
    }
    if (overlay) {
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
      return;
    }
    if (el.parentElement !== host) {
      host.appendChild(el);
    }
  }

  private releaseVideoOverlays(): void {
    this.placeOverlay(
      this.playbackPanelRef?.nativeElement,
      this.playbackPanelHostRef?.nativeElement,
      false,
    );
    this.placeOverlay(
      this.alertVideoWrapRef?.nativeElement,
      this.alertVideoHostRef?.nativeElement,
      false,
    );
    document.body.classList.remove('vp-video-fs');
  }

  private syncMapChromeForPlayback(active: boolean): void {
    this.mapNavigationService.setToolbarHidden(active);
  }

  selectPlaybackCamera(camera: 'driver' | 'front' | 'rear'): void {
    if (!this.showPlaybackPlayer || this.isWakingUp) {
      return;
    }
    if (!this.videoService.connexion) {
      this.videoService.onConnectClick(this.dataManagementService.selectedVehicule);
      return;
    }
    this.selectedCamera = camera;
    this.bindPlaybackVideoElement();
    const channel = this.getPlaybackChannel(camera);
    const wallMs = this.trackPlaybackWallMs ?? this.trackSegmentStartMs;
    if (wallMs == null) {
      return;
    }
    const aligned = this.alignSeekWallMs(wallMs);
    const token = formatPlaybackSeekToken(new Date(aligned));
    this.playbackAnchorWallMs = aligned;
    this.playbackAnchorRealMs = Date.now();
    this.trackPlaybackWallMs = aligned;
    this.trackPlaybackProgress = this.progressFromWallTime(aligned);
    this.lastEmittedPlaybackSecond = -1;
    void this.videoService.Playback(token, channel);
  }

  onTimelinePlaybackSeek(event: Event): void {
    const target = event.target as HTMLInputElement;
    const percentage = parseFloat(target.value);
    if (isNaN(percentage)) {
      return;
    }
    this.applyPlaybackProgress(percentage, false);
  }

  onTimelinePlaybackSeekCommit(event: Event): void {
    const target = event.target as HTMLInputElement;
    const percentage = parseFloat(target.value);
    if (isNaN(percentage)) {
      return;
    }
    this.applyPlaybackProgress(percentage, true);
  }

  private applyPlaybackProgress(percentage: number, commitVideo: boolean): void {
    const pct = Math.max(0, Math.min(100, percentage));
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null || t1 <= t0) {
      return;
    }

    let wallMs = Math.round(t0 + ((t1 - t0) * pct) / 100);
    if (commitVideo) {
      wallMs = this.alignSeekWallMs(wallMs);
    }
    const alignedPct = this.progressFromWallTime(wallMs);
    this.trackPlaybackProgress = alignedPct;
    this.trackPlaybackWallMs = wallMs;

    if (!commitVideo) {
      this.playbackAnchorWallMs = wallMs;
      this.playbackAnchorRealMs = null;
      return;
    }

    this.sendPlaybackSeekRequest(wallMs, alignedPct);
  }

  private sendPlaybackSeekRequest(wallMs: number, alignedPct: number): void {
    if (!this.videoService.connexion || this.isWakingUp) {
      return;
    }
    if (!this.videoService.playback && !this.videoService.loadingStream) {
      return;
    }
    const channel = this.getPlaybackChannel(this.selectedCamera);
    const token = formatPlaybackSeekToken(new Date(wallMs));
    const infos = `PNormal/${channel}/${channel}REC${token}`;
    this.playbackAnchorWallMs = wallMs;
    this.playbackAnchorRealMs = Date.now();
    this.trackPlaybackProgress = alignedPct;
    this.trackPlaybackWallMs = wallMs;
    this.lastEmittedPlaybackSecond = -1;
    this.videoService.playbackSeekToken = token;
    this.videoService.sendPlaybackRequest(infos);
    this.startVideoPlaybackTickLoop();
  }

  private alignSeekWallMs(seekMs: number): number {
    const match = findPlaybackMapRangeContaining(seekMs, this.ranges)
      || this.activeRecordingRange;
    if (!match) {
      return seekMs;
    }
    const startMs = parsePlaybackMapTokenToMillis(match.from);
    const endMs = parsePlaybackMapTokenToMillis(match.to);
    return alignSeekToPlaybackChunkStart(seekMs, startMs, endMs);
  }

  private progressFromWallTime(wallMs: number): number {
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null || t1 <= t0) {
      return 0;
    }
    const clamped = Math.max(t0, Math.min(t1, wallMs));
    return ((clamped - t0) / (t1 - t0)) * 100;
  }

  private getPlaybackChannel(camera: 'driver' | 'front' | 'rear'): string {
    const map = { driver: 'CH2', front: 'CH1', rear: 'CH3' };
    return map[camera];
  }

  private bindPlaybackVideoElement(): void {
    if (!this.playbackVideoRef?.nativeElement) {
      return;
    }
    const video = this.playbackVideoRef.nativeElement;
    video.muted = true;
    video.playsInline = true;
    this.videoService.setVideoElement(this.playbackVideoRef);
  }

  private startVideoPlaybackTickLoop(): void {
    this.stopVideoPlaybackTickLoop();
    this.lastEmittedPlaybackSecond = -1;
    this.videoPlaybackTickInterval = setInterval(() => {
      this.syncPlaybackPositionFromClock();
    }, 1000);
  }

  private stopVideoPlaybackTickLoop(): void {
    if (this.videoPlaybackTickInterval) {
      clearInterval(this.videoPlaybackTickInterval);
      this.videoPlaybackTickInterval = null;
    }
    this.lastEmittedPlaybackSecond = -1;
  }

  /**
   * Advance the timeline playhead from wall-clock while WebRTC plays.
   * MediaStream `video.currentTime` is unreliable on mobile WebViews.
   */
  private syncPlaybackPositionFromClock(): void {
    if (!this.showPlaybackPlayer || this.playbackAnchorWallMs == null) {
      return;
    }
    if (!this.videoService.playback && !this.videoService.loadingStream) {
      return;
    }
    if (this.playbackAnchorRealMs == null) {
      if (this.videoService.playback) {
        this.playbackAnchorRealMs = Date.now();
      }
      return;
    }
    const elapsedSeconds = Math.floor((Date.now() - this.playbackAnchorRealMs) / 1000);
    if (elapsedSeconds === this.lastEmittedPlaybackSecond) {
      return;
    }
    this.lastEmittedPlaybackSecond = elapsedSeconds;
    const t1 = this.trackSegmentEndMs;
    let wallMs = this.playbackAnchorWallMs + elapsedSeconds * 1000;
    if (t1 != null && wallMs > t1) {
      wallMs = t1;
    }
    this.trackPlaybackWallMs = wallMs;
    this.trackPlaybackProgress = this.progressFromWallTime(wallMs);
  }

  onTimelineEventFlagClick(ef: TimelineEventFlag, ev: Event): void {
    ev.stopPropagation();
    ev.preventDefault();
    this.selectedTimelineEvent = ef.event;
    this.selectedTimelineEventVideoUrl = this.constructAlertVideoUrl(ef.event);
    setTimeout(() => {
      const panel = document.querySelector('.vp-timeline-event-panel') as HTMLElement | null;
      this.draggableSheet?.scrollToElement(panel);
    }, 80);
  }

  clearTimelineEventDetails(): void {
    this.detachAlertVideoFullscreenListeners();
    this.placeOverlay(
      this.alertVideoWrapRef?.nativeElement,
      this.alertVideoHostRef?.nativeElement,
      false,
    );
    this.isAlertVideoFullscreen = false;
    this.syncOrientationSession();
    document.body.classList.toggle('vp-video-fs', this.isVideoExpanded());
    this.selectedTimelineEvent = null;
    this.selectedTimelineEventVideoUrl = '';
  }

  exitAlertVideoFullscreen(ev?: Event): void {
    ev?.stopPropagation();
    ev?.preventDefault();
    this.isAlertVideoFullscreen = false;
    this.iosFakeLandscape = false;
    this.iosRotateLeft = false;
    this.isDeviceLandscape = false;
    this.syncOrientationSession();
    this.cleanupOrphanedVideoOverlays();
    document.body.classList.remove('vp-video-fs');
    setTimeout(() => {
      this.refreshPlaybackVideoLayout();
      this.finishRestoreAfterFullscreen('alert');
    }, 0);
  }

  private attachAlertVideoFullscreenListeners(video: HTMLVideoElement): void {
    if (this.alertVideoEl === video) {
      return;
    }
    this.detachAlertVideoFullscreenListeners();
    this.alertVideoEl = video;
    video.addEventListener('webkitbeginfullscreen', this.onAlertVideoEnterFullscreen);
    video.addEventListener('webkitendfullscreen', this.onAlertVideoExitFullscreen);
    document.addEventListener('fullscreenchange', this.onAlertVideoFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onAlertVideoFullscreenChange);
  }

  private detachAlertVideoFullscreenListeners(): void {
    const video = this.alertVideoEl;
    if (video) {
      video.removeEventListener('webkitbeginfullscreen', this.onAlertVideoEnterFullscreen);
      video.removeEventListener('webkitendfullscreen', this.onAlertVideoExitFullscreen);
    }
    document.removeEventListener('fullscreenchange', this.onAlertVideoFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onAlertVideoFullscreenChange);
    this.alertVideoEl = null;
  }

  private readonly onAlertVideoEnterFullscreen = (): void => {
    const video = this.alertVideoEl as (HTMLVideoElement & {
      webkitExitFullscreen?: () => void;
    }) | null;

    // iOS AVPlayer ignores Capacitor orientation — switch to in-app overlay we can rotate.
    if (Capacitor.getPlatform() === 'ios' && video?.webkitExitFullscreen) {
      this.ignoringNativeFullscreenExit = true;
      try {
        video.webkitExitFullscreen();
      } catch {
        this.ignoringNativeFullscreenExit = false;
      }
      this.enterAlertInAppFullscreen();
      return;
    }

    // Android / web: keep native fullscreen, unlock so the clip can rotate with the phone.
    void this.videoOrientationService.unlockOrientation();
  };

  private enterAlertInAppFullscreen(): void {
    this.isAlertVideoFullscreen = true;
    this.syncOrientationSession();
    setTimeout(() => this.refreshPlaybackVideoLayout(), 0);
  }

  private readonly onAlertVideoExitFullscreen = (): void => {
    if (this.ignoringNativeFullscreenExit) {
      this.ignoringNativeFullscreenExit = false;
      return;
    }
    // Native fullscreen closed (Android/web).
    this.isAlertVideoFullscreen = false;
    this.iosFakeLandscape = false;
    this.iosRotateLeft = false;
    this.syncOrientationSession();
    void this.videoOrientationService.restorePortraitIfIdle();
    this.cleanupOrphanedVideoOverlays();
    document.body.classList.remove('vp-video-fs');
    setTimeout(() => {
      this.refreshPlaybackVideoLayout();
      this.finishRestoreAfterFullscreen('alert');
    }, 0);
  };

  private readonly onAlertVideoFullscreenChange = (): void => {
    if (this.isNativeAlertVideoFullscreen()) {
      this.onAlertVideoEnterFullscreen();
    } else {
      this.onAlertVideoExitFullscreen();
    }
  };

  private isNativeAlertVideoFullscreen(): boolean {
    const video = this.alertVideoEl;
    if (!video) {
      return false;
    }
    const doc = document as Document & { webkitFullscreenElement?: Element | null };
    const fullscreenEl = document.fullscreenElement || doc.webkitFullscreenElement;
    if (fullscreenEl) {
      return fullscreenEl === video || video.contains(fullscreenEl);
    }
    return !!(video as HTMLVideoElement & { webkitDisplayingFullscreen?: boolean }).webkitDisplayingFullscreen;
  }

  getEventTypeKey(type: number): string {
    const eventTypeMap: { [key: number]: string } = {
      0: 'ALGO_WARN_TYPE_NONE',
      257: 'ALGO_WARN_TYPE_FCW',
      258: 'ALGO_WARN_TYPE_FPW',
      259: 'ALGO_WARN_TYPE_LDW',
      260: 'ALGO_WARN_TYPE_VIRTUAL_BUMPERS',
      265: 'ALGO_WARN_TYPE_CALLPHONE',
      266: 'ALGO_WARN_TYPE_SMOKING',
      267: 'ALGO_WARN_TYPE_DISTRACTION',
      268: 'ALGO_WARN_TYPE_TIRED',
      270: 'ALGO_WARN_TYPE_SEATBELT',
      276: 'ALGO_WARN_TYPE_EMERGENCY',
      233: 'ALGO_WARN_HIGH_RPM',
      234: 'ALGO_WARN_SPEEDING',
      235: 'ALGO_WARN_BRAKE',
      236: 'ALGO_WARN_ROT'
    };
    return eventTypeMap[type] || 'ALGO_WARN_TYPE_NONE';
  }

  getEventIonIcon(type: number): string {
    const iconMap: { [key: number]: string } = {
      257: 'car-outline',
      258: 'walk-outline',
      259: 'navigate-outline',
      260: 'shield-outline',
      265: 'call-outline',
      266: 'flame-outline',
      267: 'eye-off-outline',
      268: 'bed-outline',
      270: 'person-remove-outline',
      276: 'warning-outline',
      233: 'speedometer-outline',
      234: 'arrow-forward-outline',
      235: 'arrow-back-outline',
      236: 'refresh-outline',
      0: 'information-circle-outline'
    };
    return iconMap[type] || 'alert-circle-outline';
  }

  getEventColorStyle(addedInfo: number): string {
    const palette = [
      '#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b',
      '#91e8e1', '#4498a1', '#f7a35c', '#8085e9', '#f15c80'
    ];
    const colorIndexMap: { [key: number]: number } = {
      257: 0, 258: 1, 259: 2, 260: 3, 265: 4, 266: 5, 267: 6, 268: 7,
      270: 8, 276: 9, 233: 10, 234: 11, 235: 12, 236: 13
    };
    const idx = colorIndexMap[addedInfo];
    return idx !== undefined ? palette[idx] : '#777';
  }

  getEventDescription(event: any): string {
    if (!event) {
      return '';
    }
    const key = this.getEventTypeKey(event.addedInfo);
    const desc = this.translate.instant('EVENTS.DESCRIPTIONS.' + key);
    if (desc && typeof desc === 'string' && !desc.startsWith('EVENTS.')) {
      return desc;
    }
    return this.translate.instant('EVENTS.DESCRIPTIONS.ALGO_WARN_TYPE_NONE');
  }

  onTimelinePointerMove(ev: PointerEvent): void {
    if (this.windowStartMs == null || this.windowEndMs == null || this.zoomSelecting) {
      return;
    }
    if (this.showPlaybackPlayer) {
      return;
    }
    // Hover date on coarse pointers (touch) is noisy; keep for mouse/pen.
    if (ev.pointerType === 'touch') {
      return;
    }
    const trackEl = ev.currentTarget as HTMLElement;
    const ratio = this.ratioFromClientX(trackEl, ev.clientX);
    this.hoverLeftPct = ratio * 100;
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const wallMs = Math.round(w0 + ratio * (w1 - w0));
    this.hoverDateLabel = formatCameraGmtDateTime(wallMs, true);
    this.hoverActive = true;
  }

  onTimelinePointerLeave(): void {
    this.clearTimelineHover();
  }

  timelineZoomPointerDown(ev: PointerEvent): void {
    const el = ev.target as HTMLElement;
    if (el.closest('.vp-timeline-overlay') || el.closest('.vp-timeline-event-flag')) {
      return;
    }
    if (el.closest('.vp-timeline-playback-seek-wrap')) {
      return;
    }
    // Zoom disabled while camera playback is open (fleet-12).
    if (this.showPlaybackPlayer) {
      return;
    }
    if (ev.button != null && ev.button !== 0) {
      return;
    }
    if (this.windowStartMs == null || this.windowEndMs == null) {
      return;
    }
    ev.preventDefault();
    this.clearTimelineHover();
    this.detachTimelineZoomListeners();
    this.zoomTrackEl = ev.currentTarget as HTMLElement;
    this.zoomSelecting = true;
    const ratio = this.ratioFromClientX(this.zoomTrackEl, ev.clientX);
    const pct = ratio * 100;
    this.zoomDragStartPct = pct;
    this.zoomDragCurrentPct = pct;
    try {
      this.zoomTrackEl.setPointerCapture(ev.pointerId);
    } catch {
      // ignore
    }
    document.addEventListener('pointermove', this.boundZoomMove);
    document.addEventListener('pointerup', this.boundZoomUp);
    document.addEventListener('pointercancel', this.boundZoomUp);
  }

  private onTimelineZoomPointerMove(ev: PointerEvent): void {
    if (!this.zoomSelecting || !this.zoomTrackEl) {
      return;
    }
    const ratio = this.ratioFromClientX(this.zoomTrackEl, ev.clientX);
    this.zoomDragCurrentPct = ratio * 100;
  }

  private onTimelineZoomPointerUp(_ev: PointerEvent): void {
    document.removeEventListener('pointermove', this.boundZoomMove);
    document.removeEventListener('pointerup', this.boundZoomUp);
    document.removeEventListener('pointercancel', this.boundZoomUp);
    this.zoomTrackEl = null;
    if (!this.zoomSelecting || this.windowStartMs == null || this.windowEndMs == null) {
      this.zoomSelecting = false;
      this.zoomDragStartPct = null;
      this.zoomDragCurrentPct = null;
      return;
    }
    this.zoomSelecting = false;
    const startPct = this.zoomDragStartPct;
    const endPct = this.zoomDragCurrentPct;
    this.zoomDragStartPct = null;
    this.zoomDragCurrentPct = null;
    if (startPct == null || endPct == null) {
      return;
    }
    const dragWidthPct = Math.abs(endPct - startPct);
    if (dragWidthPct < 1.5) {
      return;
    }
    const v0 = this.effectiveTimelineStart();
    const v1 = this.effectiveTimelineEnd();
    const span = Math.max(1, v1 - v0);
    const loPct = Math.max(0, Math.min(100, Math.min(startPct, endPct)));
    const hiPct = Math.max(0, Math.min(100, Math.max(startPct, endPct)));
    let tA = Math.round(v0 + (loPct / 100) * span);
    let tB = Math.round(v0 + (hiPct / 100) * span);
    if (tB <= tA) {
      return;
    }
    const minSpanMs = 30_000;
    if (tB - tA < minSpanMs) {
      const mid = (tA + tB) / 2;
      tA = Math.round(mid - minSpanMs / 2);
      tB = Math.round(mid + minSpanMs / 2);
    }
    const cal0 = this.windowStartMs;
    const cal1 = this.windowEndMs;
    this.timelineZoomStartMs = Math.max(cal0, Math.min(cal1, tA));
    this.timelineZoomEndMs = Math.max(cal0, Math.min(cal1, tB));
    if (this.timelineZoomEndMs <= this.timelineZoomStartMs) {
      this.timelineZoomStartMs = null;
      this.timelineZoomEndMs = null;
      return;
    }
    this.rebuildTimeline();
  }

  private rebuildTimeline(): void {
    this.timelineSegments = [];
    this.timelineTicks = [];
    if (this.windowStartMs == null || this.windowEndMs == null) {
      this.timelineEventFlags = [];
      return;
    }
    this.syncTimelineAxisLabelsFromView();
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    const minBarPct = 0.35;

    for (const r of this.ranges) {
      const a = parsePlaybackMapTokenToMillis(r.from);
      const b = parsePlaybackMapTokenToMillis(r.to);
      const clipA = Math.max(w0, Math.min(w1, a));
      const clipB = Math.max(w0, Math.min(w1, b));
      if (clipB <= clipA) {
        continue;
      }
      const leftPct = ((clipA - w0) / span) * 100;
      let widthPct = ((clipB - clipA) / span) * 100;
      if (widthPct < minBarPct) {
        widthPct = minBarPct;
      }
      this.timelineSegments.push({
        range: r,
        leftPct,
        widthPct,
        tooltip: `${formatCameraGmtDateTime(a, true)} → ${formatCameraGmtDateTime(b, true)} (${this.segmentDurationLabel(r)})`
      });
    }

    this.rebuildTimelineTicks(w0, w1, span);

    this.rebuildEventFlagsInWindow();
  }

  private rebuildTimelineTicks(w0: number, w1: number, span: number): void {
    const start = new Date(w0);
    const end = new Date(w1);
    const sameDay =
      start.getUTCFullYear() === end.getUTCFullYear() &&
      start.getUTCMonth() === end.getUTCMonth() &&
      start.getUTCDate() === end.getUTCDate();

    const dayMs = 86_400_000;
    const days = span / dayMs;
    // Keep labels readable on a phone-width timeline (avoid overlapping DD/MM HH:mm).
    let tickCount = 3;
    if (sameDay || days <= 1) {
      tickCount = 5;
    } else if (days <= 4) {
      tickCount = 4;
    }

    const divisions = Math.max(1, tickCount - 1);
    for (let i = 0; i <= divisions; i++) {
      const t = w0 + (span * i) / divisions;
      const leftPct = (i / divisions) * 100;
      const parts = formatCameraGmtTickParts(t);
      const align: TimelineTick['align'] =
        i === 0 ? 'start' : i === divisions ? 'end' : 'center';
      this.timelineTicks.push({
        leftPct,
        align,
        dateLabel: sameDay ? '' : parts.dateLabel,
        timeLabel: parts.timeLabel
      });
    }
  }

  private rebuildEventFlagsInWindow(): void {
    this.timelineEventFlags = [];
    if (this.windowStartMs == null || this.windowEndMs == null || !this.periodEvents?.length) {
      return;
    }
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    for (const ev of this.periodEvents) {
      if (!ev?.date) {
        continue;
      }
      const wallMs = moment(ev.date).valueOf();
      if (wallMs < w0 || wallMs > w1) {
        continue;
      }
      if (!this.isRecognizedEvent(ev.addedInfo)) {
        continue;
      }
      const leftPct = ((wallMs - w0) / span) * 100;
      this.timelineEventFlags.push({
        wallMs,
        leftPct,
        addedInfo: ev.addedInfo,
        ionIcon: this.getEventIonIcon(ev.addedInfo),
        color: this.getEventColorStyle(ev.addedInfo),
        title: this.buildEventFlagTitle(ev),
        event: ev
      });
    }
  }

  private buildEventFlagTitle(ev: any): string {
    const t = formatCameraGmtDateTime(moment(ev.date).valueOf(), true);
    const key = this.getEventTypeKey(ev.addedInfo);
    const desc = this.translate.instant('EVENTS.DESCRIPTIONS.' + key);
    if (desc && typeof desc === 'string' && !desc.startsWith('EVENTS.')) {
      return `${t} — ${desc}`;
    }
    return t;
  }

  private applyWindowFromHistoricalDates(): boolean {
    const start = this.historicalService.startDate;
    const end = this.historicalService.endDate;
    if (!start || !end) {
      this.windowStartMs = null;
      this.windowEndMs = null;
      return false;
    }
    let w0 = calendarFieldsAsCameraGmtMillis(start);
    let w1 = calendarFieldsAsCameraGmtMillis(end);
    if (!Number.isFinite(w0) || !Number.isFinite(w1)) {
      this.windowStartMs = null;
      this.windowEndMs = null;
      return false;
    }
    if (w1 < w0) {
      const tmp = w0;
      w0 = w1;
      w1 = tmp;
    }
    this.windowStartMs = w0;
    this.windowEndMs = w1;
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;
    this.syncTimelineAxisLabelsFromView();
    this.rebuildTimeline();
    return true;
  }

  private syncTimelineAxisLabelsFromView(): void {
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    this.timelineDisplayStart = formatCameraGmtDateTime(w0, false);
    this.timelineDisplayEnd = formatCameraGmtDateTime(w1, false);
  }

  private effectiveTimelineStart(): number {
    if (this.windowStartMs == null) {
      return 0;
    }
    return this.timelineZoomStartMs ?? this.windowStartMs;
  }

  private effectiveTimelineEnd(): number {
    if (this.windowEndMs == null) {
      return 0;
    }
    return this.timelineZoomEndMs ?? this.windowEndMs;
  }

  private ratioFromClientX(trackEl: HTMLElement, clientX: number): number {
    const rect = trackEl.getBoundingClientRect();
    return Math.max(0, Math.min(1, rect.width > 0 ? (clientX - rect.left) / rect.width : 0));
  }

  private clearTimelineHover(): void {
    this.hoverActive = false;
    this.hoverLeftPct = 0;
    this.hoverDateLabel = '';
  }

  private detachTimelineZoomListeners(): void {
    document.removeEventListener('pointermove', this.boundZoomMove);
    document.removeEventListener('pointerup', this.boundZoomUp);
    document.removeEventListener('pointercancel', this.boundZoomUp);
    this.zoomTrackEl = null;
    if (this.zoomSelecting) {
      this.zoomSelecting = false;
      this.zoomDragStartPct = null;
      this.zoomDragCurrentPct = null;
    }
  }

  private subscribeToCameraWakeupState(): void {
    this.wakeupSubscription = this.videoService.isWakingUp$.subscribe((waking) => {
      this.isWakingUp = waking;
    });
    this.wakeupTimeSubscription = this.videoService.wakeupTimeRemaining$.subscribe((remaining) => {
      this.wakeupTimeRemaining = remaining;
    });
  }

  private isRecognizedEvent(type: number): boolean {
    if (type == null || isNaN(+type)) {
      return false;
    }
    const recognizedTypes = [0, 257, 258, 259, 260, 265, 266, 267, 268, 270, 276, 233, 234, 235, 236];
    return recognizedTypes.includes(+type);
  }

  private constructAlertVideoUrl(event: any): string {
    const vehicule = this.dataManagementService.selectedVehicule;
    if (!vehicule?.device?.streamId || !event?.date) {
      return '';
    }
    const eventInstant = new Date(event.date);
    if (isNaN(eventInstant.getTime())) {
      return '';
    }
    let alertId = event.addedInfo;
    if (event.addedInfo >= 256 && event.addedInfo <= 260) {
      alertId = 4;
    } else if (event.addedInfo >= 265 && event.addedInfo <= 270) {
      alertId = 5;
    }
    const { dateStr, timeStr } = formatAlertClipFilenameParts(eventInstant);
    return `${videoUrl}${vehicule.device.streamId}/alert/alert-${alertId}-${dateStr}-${timeStr}.mp4`;
  }

  private async showToast(message: string, color: string): Promise<void> {
    const toast = await this.toastController.create({ message, duration: 3500, color, position: 'bottom' });
    await toast.present();
  }
}
